// Columns configuration for Tag Registers
// Built from TagHarvester Excel title mappings to ensure consistency

import {
    equipmentExcelTitlesMap,
    pipelineExcelTitlesMap,
    structuralExcelTitlesMap,
    inspectionExcelTitlesMap,
    anomalyExcelTitlesMap,
    annotationExcelTitlesMap,
    scaffoldExcelTitlesMap,
    permitExcelTitlesMap,
    containerExcelTitlesMap,
} from '@/features/TagHarvester/utils/tagsExcelTitles';

// Helper to build column objects from an Excel mapping
function buildColumnsFromExcelMap(excelMap, options = {}) {
    const {
        excludeKeys = [],
        hiddenByDefault = true,
        startOrder = 3,
    } = options;

    let order = startOrder;

    return Object.entries(excelMap)
        .filter(([title]) => !excludeKeys.includes(title))
        .map(([title, property]) => ({
            text: title,
            value: property,
            hidden: hiddenByDefault,
            sortable: true,
            order: order++,
            hidable: true,
            width: 180,
        }));
}

// Common leading columns across registers
const COMMON_LEADING_COLUMNS = [{
    text: '', value: 'data-table-select', hidden: false, width: 50, sortable: false, order: -1, hidable: false,
}, {
    text: 'Actions', value: 'actions', hidden: false, width: 110, sortable: false, order: 0, hidable: false,
}, {
    text: 'Tag Name', value: 'name', hidden: false, width: 220, sortable: true, order: 1, hidable: false,
}];

// Equipment
export const EQUIPMENT_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(equipmentExcelTitlesMap, {
        // Excel title that maps to name
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Pipeline
export const PIPELINE_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(pipelineExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Structural
export const STRUCTURAL_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(structuralExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Inspection
export const INSPECTION_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(inspectionExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Anomaly
export const ANOMALY_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(anomalyExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Annotation
export const ANNOTATION_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(annotationExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Scaffold
export const SCAFFOLD_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(scaffoldExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Permit — its own column set, not Scaffold's (AVA-493 WS-5 / AC-13). Each
// buildColumnsFromExcelMap call returns fresh column objects, so no column is shared with
// another type even while the underlying titles are still identical.
export const PERMIT_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(permitExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Container
export const CONTAINER_COLUMNS = [
    ...COMMON_LEADING_COLUMNS,
    ...buildColumnsFromExcelMap(containerExcelTitlesMap, {
        excludeKeys: ['Tag No.*'],
        hiddenByDefault: true,
        startOrder: 3,
    }),
];

// Convenience map
export const REGISTER_COLUMNS_BY_TYPE = {
    Equipment: EQUIPMENT_COLUMNS,
    Pipeline: PIPELINE_COLUMNS,
    Structural: STRUCTURAL_COLUMNS,
    Inspection: INSPECTION_COLUMNS,
    Anomaly: ANOMALY_COLUMNS,
    Annotation: ANNOTATION_COLUMNS,
    Scaffold: SCAFFOLD_COLUMNS,
    Permit: PERMIT_COLUMNS,
    Container: CONTAINER_COLUMNS,
};

export function getRegisterColumnsForType(tagType) {
    return REGISTER_COLUMNS_BY_TYPE[tagType] || [];
}


